import { apiClient } from '@littlebird/auth-client';
import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { toast } from 'sonner';

import { getEnvVar } from '@/env';
import {
  checkoutClient,
  subscriptionApis,
} from '@/features/subscription/subscription.query';
import { queryClient } from '@/query/client';
import { userStore } from '@/stores/user.store';
import { trackEvent } from '@/utils/analytics';
import { inBrowser } from '@/utils/capabilities';
import { handleExternalUrlClick } from '@/utils/platform';

import { teamStore } from './team.store';
import {
  DiscoverableTeamSchema,
  IndividualPlanCreditSchema,
  TeamAutoReloadSettingsSchema,
  TeamDashboardDataSchema,
  TeamMemberSchema,
  TeamPricingSchema,
  TeamSsoSchema,
  TeamUsageResponseSchema,
  TeamWalletResponseSchema,
  type CreateTeamRequest,
  type DiscoverableTeam,
  type IndividualPlanCredit,
  type EnforcedExcludedApp,
  type InviteMemberRequest,
  type MyTeam,
  type MeetingRuleDraft,
  type TeamAppList,
  type TeamAutoReloadSettings,
  type TeamDashboardData,
  type TeamMember,
  type TeamSso,
  type TeamUsageResponse,
  type TeamWalletResponse,
  type UpdateMemberRoleRequest,
  type UpdateMemberTierRequest,
  type UpdateTeamAutoReloadRequest,
  type UpdateTeamDataControlsRequest,
} from './team.types';

export function getInviteUrl(token: string): string {
  const base = getEnvVar('BASE_URL_FOR_PUBLIC_LINKS');
  return `${base}/team/join?token=${token}`;
}

export const teamApis = {
  async fetchDashboard({
    silent = false,
  } = {}): Promise<TeamDashboardData | null> {
    if (!silent) teamStore.setIsLoading(true);
    teamStore.setError(null);
    try {
      const data = await apiClient.get('/team/dashboard');
      const dashboard = data ? TeamDashboardDataSchema.parse(data) : null;
      teamStore.setDashboard(dashboard);
      return dashboard;
    } catch (error) {
      console.error('Failed to fetch team dashboard:', error);
      teamStore.setError(
        error instanceof Error ? error.message : 'Failed to load team data'
      );
      return null;
    } finally {
      if (!silent) teamStore.setIsLoading(false);
    }
  },

  async createCheckout(req: CreateTeamRequest): Promise<void> {
    teamStore.setCheckoutLoading(true);
    teamStore.setError(null);
    try {
      const { url } = await apiClient.post<{ url: string }>(
        '/team/create-checkout',
        { ...req, client: checkoutClient() }
      );
      if (inBrowser()) window.location.href = url;
      else handleExternalUrlClick(url);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to start checkout';
      teamStore.setError(message);
      toast.error(message);
    } finally {
      teamStore.setCheckoutLoading(false);
    }
  },

  async inviteMember(
    teamId: string,
    req: InviteMemberRequest
  ): Promise<TeamMember> {
    teamStore.setInviteLoading(true);
    teamStore.setError(null);
    try {
      const data = await apiClient.post(`/team/${teamId}/members/invite`, req);
      const member = TeamMemberSchema.parse(data);
      await teamApis.fetchDashboard({ silent: true });
      return member;
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to send invite';
      teamStore.setError(message);
      toast.error(message);
      throw error;
    } finally {
      teamStore.setInviteLoading(false);
    }
  },

  async updateMemberRole(
    teamId: string,
    memberId: string,
    req: UpdateMemberRoleRequest,
    fromRole: string
  ): Promise<void> {
    teamStore.setMutatingMemberId(memberId);
    try {
      await apiClient.patch(`/team/${teamId}/members/${memberId}/role`, req);
      trackEvent('TEAM_MEMBER_ROLE_CHANGED', {
        from_role: fromRole,
        to_role: req.role,
      });
      await teamApis.fetchDashboard({ silent: true });
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to update role'
      );
    } finally {
      teamStore.setMutatingMemberId(null);
    }
  },

  async updateMemberTier(
    teamId: string,
    memberId: string,
    req: UpdateMemberTierRequest,
    fromTier: string
  ): Promise<void> {
    teamStore.setMutatingMemberId(memberId);
    try {
      await apiClient.patch(`/team/${teamId}/members/${memberId}/tier`, req);
      trackEvent('TEAM_MEMBER_TIER_CHANGED', {
        from_tier: fromTier,
        to_tier: req.tier,
      });
      await teamApis.fetchDashboard({ silent: true });
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to update tier'
      );
    } finally {
      teamStore.setMutatingMemberId(null);
    }
  },

  async removeMember(teamId: string, memberId: string): Promise<void> {
    teamStore.setMutatingMemberId(memberId);
    try {
      await apiClient.delete(`/team/${teamId}/members/${memberId}`);
      trackEvent('TEAM_MEMBER_REMOVED');
      await teamApis.fetchDashboard({ silent: true });
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to remove member'
      );
    } finally {
      teamStore.setMutatingMemberId(null);
    }
  },

  async transferOwnership(teamId: string, membershipId: string): Promise<void> {
    try {
      await apiClient.post(`/team/${teamId}/transfer-ownership`, {
        membership_id: membershipId,
      });
      await teamApis.fetchDashboard({ silent: true });
      toast.success('Ownership transferred', {
        description:
          'Billing emails are unchanged. Update them from Manage Billing.',
      });
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to transfer ownership'
      );
      throw error;
    }
  },

  async joinTeam(token: string): Promise<void> {
    teamStore.setJoinLoading(true);
    teamStore.setError(null);
    try {
      await apiClient.post('/team/join', { token });
      trackEvent('TEAM_INVITE_ACCEPTED');
      await teamApis.fetchDashboard({ silent: true });
      teamStore.setDiscoverableTeam(null);
      subscriptionApis.getStatus();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to join team';
      teamStore.setError(message);
      toast.error(message);
      throw error;
    } finally {
      teamStore.setJoinLoading(false);
    }
  },

  async deleteTeam(teamId: string): Promise<void> {
    try {
      await apiClient.delete(`/team/${teamId}`);
      trackEvent('TEAM_DELETED');
      teamStore.setDashboard(null);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to delete team'
      );
      throw error;
    }
  },

  async createPortal(teamId: string): Promise<void> {
    teamStore.setPortalLoading(true);
    try {
      const { url } = await apiClient.post<{ url: string }>(
        `/team/${teamId}/billing/portal`,
        { client: checkoutClient() }
      );
      trackEvent('TEAM_BILLING_PORTAL_OPENED');
      if (inBrowser()) window.location.href = url;
      else handleExternalUrlClick(url);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to open billing portal'
      );
    } finally {
      teamStore.setPortalLoading(false);
    }
  },

  async fetchDiscoverableTeam(): Promise<DiscoverableTeam | null> {
    try {
      const data = await apiClient.get('/team/discoverable');
      const team = data ? DiscoverableTeamSchema.parse(data) : null;
      teamStore.setDiscoverableTeam(team);
      return team;
    } catch (error) {
      console.error('Failed to fetch discoverable team:', error);
      return null;
    }
  },

  async fetchPricing(): Promise<void> {
    try {
      const data = await apiClient.get('/team/pricing?v=2');
      const parsed = TeamPricingSchema.parse(data);
      teamStore.setPricing(parsed.tiers);
    } catch (error) {
      console.error('Failed to fetch team pricing:', error);
    }
  },

  async fetchIndividualPlanCredit(): Promise<IndividualPlanCredit | null> {
    try {
      const data = await apiClient.get('/team/individual-plan-credit');
      return IndividualPlanCreditSchema.parse(data);
    } catch (error) {
      console.error('Failed to fetch individual plan credit:', error);
      return null;
    }
  },

  async addAllowedDomain(teamId: string, domain: string): Promise<void> {
    teamStore.setMutatingDomain(domain);
    try {
      await apiClient.post(`/team/${teamId}/domains`, { domain });
      trackEvent('TEAM_DOMAIN_ADDED');
      await teamApis.fetchDashboard({ silent: true });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to add domain';
      toast.error(message);
      throw error;
    } finally {
      teamStore.setMutatingDomain(null);
    }
  },

  async removeAllowedDomain(teamId: string, domain: string): Promise<void> {
    teamStore.setMutatingDomain(domain);
    try {
      await apiClient.delete(
        `/team/${teamId}/domains/${encodeURIComponent(domain)}`
      );
      trackEvent('TEAM_DOMAIN_REMOVED');
      await teamApis.fetchDashboard({ silent: true });
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to remove domain'
      );
    } finally {
      teamStore.setMutatingDomain(null);
    }
  },

  async addTeamApp(
    teamId: string,
    list: TeamAppList,
    app: EnforcedExcludedApp
  ): Promise<void> {
    teamStore.setMutatingDomain(app.bundle_id);
    try {
      await apiClient.post(`/team/${teamId}/${list}/apps`, app);
      await teamApis.fetchDashboard({ silent: true });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to add app';
      toast.error(message);
      throw error;
    } finally {
      teamStore.setMutatingDomain(null);
    }
  },

  async removeTeamApp(
    teamId: string,
    list: TeamAppList,
    bundleId: string
  ): Promise<void> {
    teamStore.setMutatingDomain(bundleId);
    try {
      await apiClient.delete(
        `/team/${teamId}/${list}/apps/${encodeURIComponent(bundleId)}`
      );
      await teamApis.fetchDashboard({ silent: true });
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to remove app'
      );
    } finally {
      teamStore.setMutatingDomain(null);
    }
  },

  async addEnforcedExcludedDomain(
    teamId: string,
    domain: string
  ): Promise<void> {
    teamStore.setMutatingDomain(domain);
    try {
      await apiClient.post(`/team/${teamId}/exclusions/domains`, { domain });
      await teamApis.fetchDashboard({ silent: true });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to add excluded site';
      toast.error(message);
      throw error;
    } finally {
      teamStore.setMutatingDomain(null);
    }
  },

  async removeEnforcedExcludedDomain(
    teamId: string,
    domain: string
  ): Promise<void> {
    teamStore.setMutatingDomain(domain);
    try {
      await apiClient.delete(
        `/team/${teamId}/exclusions/domains/${encodeURIComponent(domain)}`
      );
      await teamApis.fetchDashboard({ silent: true });
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to remove excluded site'
      );
    } finally {
      teamStore.setMutatingDomain(null);
    }
  },

  async addMeetingRecordingRule(
    teamId: string,
    rule: MeetingRuleDraft
  ): Promise<void> {
    try {
      await apiClient.post(`/team/${teamId}/meeting-rules`, rule);
      await teamApis.fetchDashboard({ silent: true });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to add meeting rule';
      toast.error(message);
      throw error;
    }
  },

  async removeMeetingRecordingRule(
    teamId: string,
    ruleId: string
  ): Promise<void> {
    teamStore.setMutatingDomain(ruleId);
    try {
      await apiClient.delete(`/team/${teamId}/meeting-rules/${ruleId}`);
      await teamApis.fetchDashboard({ silent: true });
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to remove meeting rule'
      );
    } finally {
      teamStore.setMutatingDomain(null);
    }
  },

  async updateDataControls(
    teamId: string,
    req: UpdateTeamDataControlsRequest
  ): Promise<void> {
    try {
      await apiClient.patch(`/team/${teamId}/data-controls`, req);
      await teamApis.fetchDashboard({ silent: true });
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to update data controls'
      );
    }
  },

  async enableBaa(teamId: string, legalName: string): Promise<void> {
    await apiClient.post(`/team/${teamId}/baa`, { legal_name: legalName });
    await teamApis.fetchDashboard({ silent: true });
    await userStore.refresh();
  },

  async joinByDomain(teamId: string): Promise<void> {
    teamStore.setJoinByDomainLoading(true);
    teamStore.setError(null);
    try {
      await apiClient.post('/team/join-by-domain', { team_id: teamId });
      trackEvent('TEAM_DOMAIN_JOIN_ACCEPTED');
      await teamApis.fetchDashboard({ silent: true });
      teamStore.setDiscoverableTeam(null);
      subscriptionApis.getStatus();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to join team';
      teamStore.setError(message);
      toast.error(message);
      throw error;
    } finally {
      teamStore.setJoinByDomainLoading(false);
    }
  },

  async getWallet(teamId: string): Promise<TeamWalletResponse> {
    const data = await apiClient.get(`/team/${teamId}/wallet`);
    return TeamWalletResponseSchema.parse(data);
  },

  async getSso(teamId: string): Promise<TeamSso> {
    return TeamSsoSchema.parse(await apiClient.get(`/team/${teamId}/sso`));
  },

  /** On web the tab opens before the await, or it would be popup-blocked */
  async openSsoSetup(teamId: string): Promise<void> {
    const setupTab = inBrowser() ? window.open('', '_blank') : null;
    try {
      const { url } = await apiClient.post<{ url: string }>(
        `/team/${teamId}/sso/ticket`
      );
      if (setupTab) setupTab.location.href = url;
      else handleExternalUrlClick(url);
      await queryClient.invalidateQueries({ queryKey: ['team-sso', teamId] });
    } catch (error) {
      setupTab?.close();
      toast.error(
        error instanceof Error ? error.message : 'Failed to start SSO setup'
      );
    }
  },

  async setSsoRequired(teamId: string, required: boolean): Promise<void> {
    try {
      const data = await apiClient.put(`/team/${teamId}/sso/required`, {
        required,
      });
      queryClient.setQueryData(['team-sso', teamId], TeamSsoSchema.parse(data));
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to update SSO'
      );
    }
  },

  async getUsage(
    teamId: string,
    { days }: { days: number }
  ): Promise<TeamUsageResponse> {
    const data = await apiClient.get(`/team/${teamId}/usage?days=${days}`);
    return TeamUsageResponseSchema.parse(data);
  },

  async updateAutoReload(
    teamId: string,
    request: UpdateTeamAutoReloadRequest
  ): Promise<TeamAutoReloadSettings | null> {
    try {
      const data = await apiClient.put(
        `/team/${teamId}/wallet/auto-reload`,
        request
      );
      toast.success(
        request.enabled ? 'Auto-reload is on.' : 'Auto-reload turned off.'
      );
      return TeamAutoReloadSettingsSchema.parse(data);
    } catch (error) {
      const message =
        (error as any)?.response?.data?.detail ||
        'Failed to update auto-reload. Please try again.';
      toast.error(message);
      return null;
    }
  },
};

export const DEFAULT_TEAM_USAGE_DAYS = 30;

const teamUsageQueryOptions = (teamId: string, days: number) =>
  queryOptions({
    queryKey: ['team-usage', teamId, days],
    queryFn: () => teamApis.getUsage(teamId, { days }),
  });

export function useTeamUsageQuery(teamId: string, days: number) {
  return useQuery({
    ...teamUsageQueryOptions(teamId, days),
    placeholderData: (previousUsage) => previousUsage,
  });
}

export function useTeamSsoQuery(teamId: string) {
  return useQuery({
    queryKey: ['team-sso', teamId],
    queryFn: () => teamApis.getSso(teamId),
    refetchOnWindowFocus: (query) =>
      query.state.data?.status === 'in_progress' ? 'always' : false,
  });
}

export const useTeamUsagePrefetch = (teamId: string) => {
  const qc = useQueryClient();
  useEffect(() => {
    void qc.prefetchQuery(
      teamUsageQueryOptions(teamId, DEFAULT_TEAM_USAGE_DAYS)
    );
  }, [qc, teamId]);
};

const myTeamQueryOptions = queryOptions({
  queryKey: ['team-dashboard'],
  queryFn: () => teamApis.fetchDashboard({ silent: true }),
});

export function useMyTeamQuery(
  enabled: boolean,
  options?: { staleTime?: number }
) {
  return useQuery({ ...myTeamQueryOptions, ...options, enabled });
}

/** The user's team, or null when they have none. Reuses a dashboard fetched in the last minute. */
export async function fetchMyTeam(): Promise<MyTeam | null> {
  const dashboard = await queryClient.fetchQuery({
    ...myTeamQueryOptions,
    staleTime: 60_000,
  });
  return dashboard?.team ?? null;
}
